import { TEAM_BUDGET_PAYMENT_COPY } from '../constants/copy'
import type { RepPaymentSummary } from '../types'

/** What the confirmation step offers the rep next: pay the invoice, or resume paying it. */
export type RepPaymentAction = 'pay' | 'continue'

/**
 * Sep 24 — the confirmation step's payment row, and its page subtitle, for the
 * registering rep of a Team Budget team. Null when there is nothing to say about a
 * payment made at registration (the rep chose Not now) — the caller keeps its own
 * Team Budget copy, unchanged.
 *
 * It describes the ONE invoice the registration issued (`rep_payment`) — the deposit,
 * or any amount up to the whole team fee — and never speaks of the registration's
 * approval: paying a deposit qualifies a team, it does not approve it.
 *
 * - loading               → nothing about payment yet (a skeleton): the wizard's own
 *                           copy of the session is stale after the pay page;
 * - could not load        → neutral: the Team Budget shows what has been paid;
 * - `unpaid`, no attempt  → invoiced, due today — PAY NOW;
 * - `unpaid`, attempt     → "Checking your payment…" while the step polls, then
 *                           "may still be processing" — CONTINUE PAYMENT (the pay
 *                           page resolves the real state); when the pay page
 *                           cannot resume it (`can_resume: false`), no action:
 *                           the payment is being confirmed — don't pay again;
 * - `paid`, whole fee     → "Team Fee Paid In Full" — nothing is left to split;
 * - `paid`, ≥ deposit     → "Deposit Paid";
 * - `paid`, below it      → "Partial Deposit Paid" — never read as qualifying;
 * - `paid`, no deposit or
 *   fee known             → "Payment Received" with what was paid, and nothing about
 *                           the deposit or the rest of the fee (F1: Team Budget was
 *                           switched off on the division after the payment);
 * - `closed`              → neutral: the details are on the Team Budget;
 * - `received`            → the payment was taken and the team registered, but it is
 *                           not on the Team Budget yet: support reviews how it applies.
 */
export interface RepPaymentPresentation {
  title: string
  description: string
  subtitle: string
  status: 'finish' | 'process'
  icon: 'check' | 'clock'
  action: RepPaymentAction | null
  /** The fresh session is still loading: show a skeleton, state nothing. */
  isLoading: boolean
}

/** Always true of a confirmed team registration — safe to show before anything loads. */
export const TEAM_REGISTRATION_SUBMITTED =
  'Congratulations! Your team registration has been submitted.'

const REST_IS_SPLIT =
  'The rest of the team fee will be split and invoiced to your players.'

const ON_THE_TEAM_BUDGET_TITLE = 'Payment Details Are On Your Team Budget'

const sentences = (...parts: string[]): string => parts.join(' ')

const pending = (
  fields: Pick<
    RepPaymentPresentation,
    'title' | 'description' | 'subtitle' | 'action'
  >
): RepPaymentPresentation => ({
  ...fields,
  status: 'process',
  icon: 'clock',
  isLoading: false,
})

export const resolveRepPaymentPresentation = ({
  repPayment,
  depositCents,
  teamFeeCents,
  isLoading,
  isUnavailable,
  isChecking,
  formatAmount,
}: {
  repPayment: RepPaymentSummary | null | undefined
  /** The division's deposit (Minimum To Qualify), in cents; null when unknown. */
  depositCents: number | null
  /** The whole team fee, pre-tax, in cents; null when unknown. */
  teamFeeCents: number | null
  /** The fresh session has not arrived yet. */
  isLoading: boolean
  /** The fresh session could not be loaded (the wizard's copy may be stale). */
  isUnavailable: boolean
  /** The step is still polling a started attempt (its first 30 seconds). */
  isChecking: boolean
  /** Renders an amount in currency units in the registration's currency. */
  formatAmount: (amount: number) => string
}): RepPaymentPresentation | null => {
  if (isLoading) {
    return {
      ...pending({
        title: '',
        description: '',
        subtitle: TEAM_REGISTRATION_SUBMITTED,
        action: null,
      }),
      isLoading: true,
    }
  }

  if (isUnavailable) {
    return pending({
      title: ON_THE_TEAM_BUDGET_TITLE,
      description:
        'Your Team Budget shows what has been paid toward the team fee.',
      subtitle: TEAM_REGISTRATION_SUBMITTED,
      action: null,
    })
  }

  if (!repPayment) {
    return null
  }

  if (repPayment.status === 'received') {
    return {
      title: TEAM_BUDGET_PAYMENT_COPY.receivedTitle,
      description: TEAM_BUDGET_PAYMENT_COPY.received,
      subtitle: sentences(
        TEAM_REGISTRATION_SUBMITTED,
        'We received your payment.'
      ),
      status: 'finish',
      icon: 'check',
      action: null,
      isLoading: false,
    }
  }

  if (repPayment.status === 'closed') {
    return pending({
      title: ON_THE_TEAM_BUDGET_TITLE,
      description:
        'Your invoice was changed on your Team Budget, where its details are.',
      subtitle: sentences(
        TEAM_REGISTRATION_SUBMITTED,
        'Your payment details are on your Team Budget.'
      ),
      action: null,
    })
  }

  const amountCents = Math.round(repPayment.subtotal * 100)
  // Paying the whole fee leaves nothing to split among the players.
  const coversTeamFee = teamFeeCents !== null && amountCents >= teamFeeCents
  // An unknown fee (Team Budget switched off since) says nothing about the rest of it.
  const restOfTheFee =
    teamFeeCents === null || coversTeamFee ? [] : [REST_IS_SPLIT]

  if (repPayment.status === 'paid') {
    if (depositCents === null || teamFeeCents === null) {
      return {
        title: TEAM_BUDGET_PAYMENT_COPY.receivedTitle,
        description: `${formatAmount(
          repPayment.subtotal
        )} received toward the team fee.`,
        subtitle: sentences(
          TEAM_REGISTRATION_SUBMITTED,
          'We received your payment.'
        ),
        status: 'finish',
        icon: 'check',
        action: null,
        isLoading: false,
      }
    }

    if (coversTeamFee) {
      return {
        title: 'Team Fee Paid In Full',
        description: `${formatAmount(
          repPayment.subtotal
        )} received — the whole team fee.`,
        subtitle: sentences(
          TEAM_REGISTRATION_SUBMITTED,
          'We received your payment for the whole team fee.'
        ),
        status: 'finish',
        icon: 'check',
        action: null,
        isLoading: false,
      }
    }

    const coversDeposit = amountCents >= depositCents

    return {
      title: coversDeposit ? 'Deposit Paid' : 'Partial Deposit Paid',
      description: coversDeposit
        ? `${formatAmount(repPayment.subtotal)} received toward the team fee.`
        : `${formatAmount(repPayment.subtotal)} of the ${formatAmount(
            depositCents / 100
          )} deposit received.`,
      subtitle: sentences(
        TEAM_REGISTRATION_SUBMITTED,
        'We received your payment.',
        REST_IS_SPLIT
      ),
      status: 'finish',
      icon: 'check',
      action: null,
      isLoading: false,
    }
  }

  if (repPayment.attempt_started) {
    return isChecking
      ? pending({
          title: 'Checking Your Payment…',
          description: 'This can take a moment.',
          subtitle: sentences(TEAM_REGISTRATION_SUBMITTED, ...restOfTheFee),
          action: null,
        })
      : repPayment.can_resume === false
      ? pending({
          title: 'Your Payment Is Being Confirmed',
          description:
            "Please don't pay again. It will show as paid once it's confirmed — contact support if it doesn't.",
          subtitle: sentences(TEAM_REGISTRATION_SUBMITTED, ...restOfTheFee),
          action: null,
        })
      : pending({
          title: 'Your Payment May Still Be Processing',
          description:
            'If you finished paying, it will show as paid shortly. If you did not, continue your payment.',
          subtitle: sentences(TEAM_REGISTRATION_SUBMITTED, ...restOfTheFee),
          action: 'continue',
        })
  }

  return pending({
    title: `Payment Invoiced — ${formatAmount(repPayment.total)} Due Today`,
    // An unpaid invoice is listed with the rep's other payments on their Home page.
    description: 'Pay it now, or later from your Home page.',
    subtitle: sentences(
      TEAM_REGISTRATION_SUBMITTED,
      'Your invoice is ready to pay.',
      ...restOfTheFee
    ),
    action: 'pay',
  })
}

/**
 * Whether PAY NOW / CONTINUE PAYMENT may still leave for /pay, judged on the
 * summary the step fetched on the click: an invoice nobody has started paying,
 * or a started payment the pay page can resume. Never a payment it cannot
 * resume ("Your Payment Is Being Confirmed"), and never anything settled.
 */
export const opensPayPage = (
  repPayment: RepPaymentSummary | null | undefined
): boolean =>
  repPayment?.status === 'unpaid' &&
  !(repPayment.attempt_started && repPayment.can_resume === false)

/**
 * Keep asking the server while a started attempt has not settled — the webhook
 * usually lands within seconds of the card being confirmed.
 */
export const shouldPollRepPayment = (
  repPayment: RepPaymentSummary | null | undefined
): boolean =>
  repPayment?.status === 'unpaid' && repPayment.attempt_started === true
